import { Form, FormikProvider, useFormik } from 'formik';
import { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { Box, Flex } from 'reflexbox';

import { useErrorBoundary, useViewerRoles } from '@treams/treams-hooks';
import * as graphql from '@treams/treams-network';
import {
	Button,
	GraphQLErrorMessage,
	Heading,
	Heading3,
	Spinner,
	useToasts,
} from '@treams/treams-ui';

import DetailsTableForm from '../../packages/kakapo-ui-kit/components/common/DetailsTableForm/DetailsTableForm';
import { DetailsTableFormValueTypes } from '../../packages/kakapo-ui-kit/components/common/DetailsTableForm/DetailsTableFormValue';
import MembersTable from '../../packages/kakapo-ui-kit/components/common/MembersTable';

function SettingsEditTeam(props: { team: graphql.Group }) {
	const { isHRBusinessPartner } = useViewerRoles();
	const { t } = useTranslation();
	const { addToast } = useToasts();
	const history = useHistory();

	const formik = useFormik({
		initialValues: {
			name: props.team.name,
		},
		onSubmit: () => {},
	});

	const [error, attempt, reset] = useErrorBoundary();

	const [update, { loading }] = graphql.useUpdateGroupMutation();

	const onUpdate = attempt(async () => {
		reset();

		await update({
			variables: {
				input: {
					id: props.team.id,
					name: formik.values.name,
					externalReferenceID: props.team.externalReferenceID ?? '',
				},
			},
		});

		addToast(t('common.settings.teams.edit.success'), {
			appearance: 'success',
			autoDismiss: true,
		});
	});

	return (
		<Flex flexDirection="column">
			<Flex flexDirection="row" alignItems="center">
				<Heading>
					{isHRBusinessPartner ? props.team.name : t('common.settings.teams.edit.title')}
				</Heading>
				<Box ml={3}>
					<Button
						small
						type="secondary"
						title={t('common.settings.teams.edit.viewGoals')}
						onClick={() => history.push(`/goals/collective/${props.team.id}`)}
					/>
				</Box>
			</Flex>
			{!isHRBusinessPartner && (
				<>
					<Flex mt={3} mb={2}>
						<Heading3>{t('common.settings.teams.create.details')}</Heading3>
					</Flex>
					<GraphQLErrorMessage error={error} />
					<FormikProvider value={formik}>
						<Form>
							<DetailsTableForm
								formValues={[
									{
										label: 'Name',
										name: 'name',
										type: DetailsTableFormValueTypes.Text,
									},
								]}
							/>
						</Form>
					</FormikProvider>

					<Flex mt={3}>
						<Button title={t('common.save')} onClick={onUpdate} loading={loading} />
					</Flex>
				</>
			)}
			<Box mt={5}>
				<MembersTable group={props.team} />
			</Box>
		</Flex>
	);
}

function SettingsEditTeamID(props: { id: string }): ReactElement {
	const { data, error, loading } = graphql.useGroupQuery({
		variables: { id: props.id },
	});

	if (loading) {
		return (
			<Flex my={4} justifyContent="center">
				<Spinner />
			</Flex>
		);
	} else if (error) {
		return <GraphQLErrorMessage error={error.graphQLErrors[0]} />;
	} else {
		return <SettingsEditTeam team={data!.group as graphql.Group} />;
	}
}

export default SettingsEditTeamID;
